import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { DataGrid, type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid";
import { ActiveChip, PartnerTypeChip } from "../../../components/StatusChip.js";
import { RowQuickActions } from "../../../components/RowQuickActions.js";
import type { Partner } from "@kappa-v2/contracts";
import { PartnersMobileCards } from "./PartnersMobileCards.js";
import { buildPartnerRowActionItems } from "./partnerRowActions.js";

interface PartnersGridProps {
  rows: Partner[];
  loading: boolean;
  onEdit: (p: Partner) => void;
  onDelete: (p: Partner) => void;
  onToggleActive: (p: Partner) => void;
  onEnroll?: (p: Partner) => void;
}

export function PartnersGrid({
  rows,
  loading,
  onEdit,
  onDelete,
  onToggleActive,
  onEnroll
}: PartnersGridProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const getMainContact = (row: Partner) =>
    row.contacts?.find((contact) => contact.isMain)?.telephoneNumber ??
    row.contacts?.[0]?.telephoneNumber ??
    "";

  const getMainEmail = (row: Partner) =>
    row.emails?.find((email) => email.isMain)?.email ?? row.emails?.[0]?.email ?? "";

  const columns: GridColDef[] = [
    {
      field: "fullName",
      headerName: t("partners.socio"),
      flex: 1,
      minWidth: 200,
      valueGetter: (_: unknown, row: Partner) => `${row.lastName} ${row.firstName}`,
      renderCell: (params: GridRenderCellParams<Partner>) => (
        <Box sx={{ minWidth: 0, width: "100%" }}>
          <Typography variant="body2" fontWeight={600} noWrap title={`${params.row.lastName} ${params.row.firstName}`}>
            {params.row.lastName} {params.row.firstName}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            {params.row.fiscalCode ?? "—"}
          </Typography>
        </Box>
      )
    },
    {
      field: "partnerType",
      headerName: t("partners.type"),
      width: 130,
      valueGetter: (_: unknown, row: Partner) => t(row.partnerType),
      renderCell: (params: GridRenderCellParams<Partner>) => (
        <PartnerTypeChip type={params.row.partnerType} />
      )
    },
    {
      field: "contact",
      headerName: t("partners.contact"),
      flex: 1,
      minWidth: 160,
      valueGetter: (_: unknown, row: Partner) => getMainContact(row) || getMainEmail(row) || "—",
      renderCell: (params: GridRenderCellParams<Partner>) => (
        <Box sx={{ minWidth: 0, width: "100%" }}>
          <Typography variant="body2" noWrap title={getMainContact(params.row) || "—"}>
            {getMainContact(params.row) || "—"}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap component="div" title={getMainEmail(params.row)}>
            {getMainEmail(params.row)}
          </Typography>
        </Box>
      )
    },
    {
      field: "active",
      headerName: t("common.status"),
      width: 100,
      renderCell: (params: GridRenderCellParams<Partner>) => (
        <ActiveChip active={params.row.active} />
      )
    },
    {
      field: "actions",
      headerName: "",
      width: 120,
      sortable: false,
      align: "right",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<Partner>) => {
        const row = params.row;
        return (
          <RowQuickActions
            ariaLabel={`Azioni su ${row.lastName} ${row.firstName}`}
            quickKeys={["edit", "receipts"]}
            items={buildPartnerRowActionItems(row, {
              onEdit,
              onDelete,
              onToggleActive,
              onEnroll,
              onNavigate: (path) => navigate(path)
            }, t)}
          />
        );
      }
    }
  ];

  if (isMobile) {
    return (
      <PartnersMobileCards
        rows={rows}
        loading={loading}
        onEdit={onEdit}
        onDelete={onDelete}
        onToggleActive={onToggleActive}
        onEnroll={onEnroll}
      />
    );
  }

  return (
    <DataGrid
      rows={rows}
      columns={columns}
      getRowId={(r) => r._id ?? Math.random().toString()}
      loading={loading}
      autoHeight
      rowHeight={60}
      pageSizeOptions={[25, 50, 100]}
      initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
      disableRowSelectionOnClick
      sx={{
        border: "none",
        "& .MuiDataGrid-cell": { borderColor: "rgba(0,0,0,.04)", display: "flex", alignItems: "center" },
        "& .MuiDataGrid-columnHeaders": { bgcolor: "#F7F5F0" }
      }}
    />
  );
}
